import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/mascot";
import { useAIAssistant } from "@/contexts/AIAssistantContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { ChatWidgetHeader } from "./ChatWidgetHeader";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";

export function AIAssistantWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    isOpen,
    isEnabled,
    isAuthenticated,
    messages,
    sessions,
    currentSessionId,
    isLoading,
    widgetMode,
    closeWidget,
    sendMessage,
    abortGeneration,
    createNewSession,
    setWidgetMode,
  } = useAIAssistant();

  const [inputValue, setInputValue] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isAIPage = location.pathname === "/my-topics/ai" || location.pathname.startsWith("/my-topics/ai/");
  const isMyTopicsPage = location.pathname === "/my-topics" || location.pathname.startsWith("/my-topics/");

  const isSidebarMode = widgetMode === "sidebar" && !isMobile;
  const SIDEBAR_WIDTH = 380;

  // The docked panel steals 380px from the page: the layout shift
  // (--ai-sidebar-width margin) only applies on wide screens (xl, >=1280px).
  // Below that, sidebar mode renders the same full-height panel as an
  // overlay in front of content, leaving the page untouched.
  const [isWideScreen, setIsWideScreen] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1280,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const onChange = () => setIsWideScreen(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const canDock = isSidebarMode && isWideScreen;

  const currentSession = sessions.find((s) => s.id === currentSessionId);
  const sessionTitle = currentSession?.title || "New Conversation";
  const isNewConversation = messages.length === 0;

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (canDock && isOpen && isEnabled && !isAIPage) {
      document.documentElement.style.setProperty(
        "--ai-sidebar-width",
        `${SIDEBAR_WIDTH}px`
      );
    } else {
      document.documentElement.style.setProperty("--ai-sidebar-width", "0px");
    }
    return () => {
      document.documentElement.style.setProperty("--ai-sidebar-width", "0px");
    };
  }, [canDock, isOpen, isEnabled, isAIPage]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();
    setInputValue("");
    setEditingMessageIndex(null);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
    await sendMessage(message, editingMessageIndex ?? undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === "Escape" && editingMessageIndex !== null) {
      setEditingMessageIndex(null);
      setInputValue("");
    }
  };

  const handleEditMessage = (messageIndex: number) => {
    const message = messages[messageIndex];
    if (message) {
      setEditingMessageIndex(messageIndex);
      setInputValue(message.content);
      inputRef.current?.focus();
    }
  };

  if (!isEnabled || isAIPage || isMyTopicsPage) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-black/50 md:hidden"
                onClick={closeWidget}
              />
            )}

            <motion.div
              initial={
                isSidebarMode
                  ? { x: SIDEBAR_WIDTH, opacity: 0 }
                  : { opacity: 0, y: isMobile ? 20 : 0 }
              }
              animate={
                isSidebarMode
                  ? { x: 0, opacity: 1 }
                  : { opacity: 1, y: 0 }
              }
              exit={
                isSidebarMode
                  ? { x: SIDEBAR_WIDTH, opacity: 0 }
                  : { opacity: 0, y: isMobile ? 20 : 0 }
              }
              transition={{ duration: 0.25, ease: "easeOut" }}
className={cn(
                 "fixed z-40 bg-card border border-border/60 flex flex-col overflow-hidden",
                isSidebarMode
                  ? "top-0 right-0 bottom-0 rounded-none border-r-0 border-t-0 border-b-0 shadow-2xl"
                  : cn(
                      "inset-x-0 top-0 bottom-14 rounded-none shadow-none",
                      "md:inset-auto md:bottom-3 md:right-3 md:rounded-2xl md:h-[600px] md:w-96 md:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.3),0_0_0_1px_rgba(0,0,0,0.05)]"
                    )
              )}
              style={isSidebarMode ? { width: SIDEBAR_WIDTH } : undefined}
            >
              <ChatWidgetHeader
                sessionTitle={sessionTitle}
                showHistory={showHistory}
                onShowHistoryChange={setShowHistory}
                onNewSession={createNewSession}
                onClose={closeWidget}
                isNewConversation={isNewConversation}
                isMobile={isMobile}
                widgetMode={widgetMode}
                onSetWidgetMode={setWidgetMode}
              />

              <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                  {isAuthenticated ? (
                    <>
                      <ChatMessages
                        messages={messages}
                        isLoading={isLoading}
                        onEditMessage={handleEditMessage}
                        sendMessage={sendMessage}
                      />

                      <ChatInput
                        value={inputValue}
                        onChange={setInputValue}
                        onKeyDown={handleKeyDown}
                        onSend={handleSend}
                        onAbort={abortGeneration}
                        isLoading={isLoading}
                        isEditing={editingMessageIndex !== null}
                        inputRef={inputRef}
                      />
                    </>
                  ) : (
                    /* The fold dropped the anonymous path — `ai-chat` requires
                       a session — so a signed-out visitor gets a sign-in prompt
                       instead of a chat that cannot send. */
                    <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-6">
                      <Mascot mood="idle" size="sm" />
                      <h4 className="font-bold text-base mb-1 mt-3">
                        {t("pages.aiAssistant.signInTitle", "Sign in to chat with Lyder")}
                      </h4>
                      <p className="text-xs text-muted-foreground mb-5 max-w-[240px] leading-relaxed">
                        {t(
                          "pages.aiAssistant.signInBody",
                          "Your learning coach keeps a history and answers about the page you are on. That needs an account.",
                        )}
                      </p>
                      <Button
                        className="gap-2"
                        onClick={() => {
                          closeWidget();
                          navigate(
                            `/auth/signin?redirectTo=${encodeURIComponent(
                              window.location.pathname + window.location.search,
                            )}`,
                          );
                        }}
                      >
                        <LogIn className="h-4 w-4" />
                        {t("common.actions.signIn", "Sign In")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
